// US-BUCKET-03 -- Add a new bucket.
//
// Guardian-initiated flow: the guardian picks a child and fills in name,
// colour, percent, fixed/flexible, and the approval/management flags.
// The form shows a live preview of which flexible buckets will shrink
// and to what, using the shared `rebalanceForNewBucket` function so the
// preview matches the backend exactly.
//
// Child-initiated flow (US-BUCKET-07) will reuse this screen with
// `actorIsChild = true`, which hides the three guardian-only flags and
// forces them to false.

import {
  type AppError,
  type BucketAddOutput,
  type BucketEntry,
  bucketAdd,
} from '@chorepop/core/client';
import { rebalanceForNewBucket } from '@chorepop/core/domain';
import { type Component, For, Show, createMemo, createSignal } from 'solid-js';
import { ActionScreen } from '../components/ActionScreen';
import { COLOUR_PALETTE, ColourPicker } from '../components/ColourPicker';
import { ToggleSwitch } from '../components/ToggleSwitch';

type Props = {
  sessionId: string;
  actorIsChild: boolean;
  childAccountId: number;
  childDisplayName: string;
  existingBuckets: BucketEntry[];
  onCreated: (result: BucketAddOutput) => void;
  onCancel: () => void;
};

export const AddBucketScreen: Component<Props> = (props) => {
  const [bucketName, setBucketName] = createSignal('');
  const [displayColor, setDisplayColor] = createSignal(COLOUR_PALETTE[0]?.value ?? '#f07a2e');
  const [percentStr, setPercentStr] = createSignal('');
  // Child-role forces all three flags to false and the form hides them.
  const [percentIsFixed, setPercentIsFixed] = createSignal(false);
  const [isGuardianManaged, setIsGuardianManaged] = createSignal(false);
  const [requiresApprovalToWithdraw, setRequiresApprovalToWithdraw] = createSignal(false);
  const [submitting, setSubmitting] = createSignal(false);
  const [errorMessage, setErrorMessage] = createSignal<string | null>(null);

  const allocationBps = createMemo(() => {
    const trimmed = percentStr().trim();
    if (trimmed.length === 0) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    return Math.round(n * 100);
  });

  const flexiblePoolBps = createMemo(() => {
    return props.existingBuckets
      .filter((b) => !b.percentIsFixed)
      .reduce((acc, b) => acc + b.allocationBps, 0);
  });

  // Live preview -- exact same function the backend will use.
  const preview = createMemo(() => {
    const bps = allocationBps();
    if (bps === null || bps <= 0 || bps >= 10000) return null;
    const existing = props.existingBuckets.map((b) => ({
      bucketId: b.bucketId,
      allocationBps: b.allocationBps,
      percentIsFixed: b.percentIsFixed,
      sortOrder: b.sortOrder,
      priority: b.priority,
    }));
    return rebalanceForNewBucket(existing, bps);
  });

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setErrorMessage(null);

    const trimmedName = bucketName().trim();
    if (trimmedName.length === 0) {
      setErrorMessage('Please enter a name for the bucket.');
      return;
    }
    if ([...trimmedName].length > 50) {
      setErrorMessage('Bucket name must be 50 characters or fewer.');
      return;
    }
    const bps = allocationBps();
    if (bps === null || bps <= 0 || bps >= 10000) {
      setErrorMessage('Percentage must be between 0.01 and 99.99.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await bucketAdd({
        sessionId: props.sessionId,
        childAccountId: props.childAccountId,
        bucketName: trimmedName,
        displayColor: displayColor() ?? null,
        allocationBps: bps,
        isGuardianManaged: props.actorIsChild ? false : isGuardianManaged(),
        requiresApprovalToWithdraw: props.actorIsChild ? false : requiresApprovalToWithdraw(),
        percentIsFixed: props.actorIsChild ? false : percentIsFixed(),
      });
      if (!result.ok) {
        setErrorMessage(humanReadableError(result.error));
        return;
      }
      setBucketName('');
      setPercentStr('');
      props.onCreated(result.value);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ActionScreen
      class="action-screen--bucket-form"
      eyebrow={`${props.childDisplayName}'s buckets`}
      title="New bucket"
      onBack={props.onCancel}
    >
      <p class="action-screen-intro">
        Pick a name, a colour, and a percentage. The percentage comes out of the flexible pool, so
        one or more existing flexible buckets will shrink to make room. Fixed buckets won't change.
      </p>

      <form onSubmit={handleSubmit} novalidate>
        <label class="field">
          <span class="field-label">Bucket name</span>
          <input
            type="text"
            autocomplete="off"
            required
            autofocus
            disabled={submitting()}
            value={bucketName()}
            onInput={(e) => {
              setBucketName(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="add-bucket-name"
          />
        </label>

        <ColourPicker value={displayColor()} onChange={setDisplayColor} disabled={submitting()} />

        <label class="field">
          <span class="field-label">Percentage</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            max="99.99"
            inputmode="decimal"
            required
            disabled={submitting()}
            value={percentStr()}
            onInput={(e) => {
              setPercentStr(e.currentTarget.value);
              setErrorMessage(null);
            }}
            data-testid="add-bucket-percent"
          />
          <span class="field-hint">
            Flexible pool available: {(flexiblePoolBps() / 100).toFixed(2)}%
          </span>
        </label>

        <Show when={!props.actorIsChild}>
          {/* BUG-021: group the bucket-behaviour flags in a fieldset
                with a legend so the three checkboxes read as a related
                set rather than a continuous wall of unrelated lines.
                The fieldset gives semantic structure (good for screen
                readers) and visible grouping (a soft border + heading). */}
          <fieldset class="bucket-flags-group">
            <legend>Bucket behaviour</legend>

            <ToggleSwitch
              label="Fixed percentage"
              description="Locked, so it won't shrink when more buckets are added later."
              checked={percentIsFixed()}
              onChange={setPercentIsFixed}
              disabled={submitting()}
              testId="add-bucket-fixed"
            />

            <ToggleSwitch
              label="Guardian-managed"
              description="Only you (the guardian) can edit or deactivate this bucket."
              checked={isGuardianManaged()}
              onChange={setIsGuardianManaged}
              disabled={submitting()}
              testId="add-bucket-guardian-managed"
            />

            <ToggleSwitch
              label="Withdrawals need approval"
              description="Your child must request your sign-off before spending from this bucket."
              checked={requiresApprovalToWithdraw()}
              onChange={setRequiresApprovalToWithdraw}
              disabled={submitting()}
              testId="add-bucket-requires-approval"
            />
          </fieldset>
        </Show>

        <Show when={preview()}>
          {(p) => (
            <Show
              when={p().ok}
              fallback={
                <div class="form-error" role="alert" data-testid="add-bucket-preview-error">
                  {formatPreviewError(p())}
                </div>
              }
            >
              <div class="rebalance-preview" data-testid="add-bucket-preview">
                <div class="preview-heading">After this change:</div>
                <ul>
                  <For
                    each={
                      (
                        p() as {
                          ok: true;
                          value: { bucketId: number; newAllocationBps: number }[];
                        }
                      ).value
                    }
                  >
                    {(rr) => {
                      const before = props.existingBuckets.find((b) => b.bucketId === rr.bucketId);
                      if (!before) return null;
                      return (
                        <li data-testid={`preview-row-${before.bucketId}`}>
                          <span class="preview-name">{before.bucketName}</span>
                          <span class="preview-change">
                            {(before.allocationBps / 100).toFixed(2)}% →{' '}
                            {(rr.newAllocationBps / 100).toFixed(2)}%
                          </span>
                        </li>
                      );
                    }}
                  </For>
                  <li class="preview-new">
                    <span class="preview-name">{bucketName() || 'New bucket'}</span>
                    <span class="preview-change">
                      + {(() => {
                        const bps = allocationBps();
                        return bps !== null ? (bps / 100).toFixed(2) : '0';
                      })()}%
                    </span>
                  </li>
                </ul>
              </div>
            </Show>
          )}
        </Show>

        <Show when={errorMessage()}>
          {(msg) => (
            <div class="form-error" role="alert" data-testid="add-bucket-error">
              {msg()}
            </div>
          )}
        </Show>

        <button
          type="submit"
          class="primary"
          disabled={submitting()}
          data-testid="add-bucket-submit"
        >
          {submitting() ? 'Adding…' : 'Add bucket'}
        </button>
      </form>
    </ActionScreen>
  );
};

function formatPreviewError(outcome: ReturnType<typeof rebalanceForNewBucket>): string {
  if (outcome.ok) return '';
  const e = outcome.error;
  switch (e.kind) {
    case 'newBucketBpsOutOfRange':
      return 'Percentage must be between 0.01 and 99.99.';
    case 'noFlexibleBuckets':
      return 'No flexible buckets available to shrink. Edit a fixed bucket first to free up room.';
    case 'flexiblePoolTooSmall':
      return `Flexible pool is only ${(e.availableBps / 100).toFixed(2)}% -- not enough room for ${(e.requestedBps / 100).toFixed(2)}%. Reduce a fixed bucket's percentage first.`;
    case 'currentAllocationsDoNotSumTo10000':
      return `Data inconsistency: current allocations sum to ${(e.actual / 100).toFixed(2)}%, expected 100%. Close and reopen this screen, or contact support.`;
  }
}

function humanReadableError(error: AppError): string {
  switch (error.kind) {
    case 'validation':
    case 'conflict':
      return error.message;
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'not_found':
      return error.message;
    case 'network_required':
      return error.message;
    default:
      return `Something went wrong: ${error.message}`;
  }
}
